import React, { useCallback, useState } from "react";
import ConfirmDialog from "../../components/ConfirmDialog";
import { notBlank } from "../../util/ext";
import { useConfirm } from "../../common/use-confirm";
import { lensPath, set } from "ramda";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { validateProgram, validateProgramField } from "../programValidation";
import Button from "@mui/material/Button";
import { useErrorBox } from "../../manage-organizations/edit/use-errorBox";
import OrganizationAutoComplete from "../../user-admin/OrganizationAutoComplete";
import { useProgramCreate } from "../cspServices";

const NewProgramDialog = ({ open, onCancel, onApply }) => {
  const confirmHandler = useConfirm();
  const { showConfirm } = confirmHandler;

  const {
    addValidationError,
    removeValidationError,
    setValidationErrors,
    setGeneralError,
    hasValidationError,
    firstValidationError,
    ErrorBox: DialogErrors,
  } = useErrorBox();
  const [program, setProgram] = useState({
    programName: null,
    programKey: null,
    organizationId: null,
  });

  const createProgram = useProgramCreate();
  const handleCreate = useCallback(async () => {
    const validationErrors = validateProgram(program);
    if (notBlank(validationErrors)) {
      setValidationErrors(validationErrors);
    } else {
      try {
        const result = await createProgram.mutateAsync({
          data: program,
        });
        onApply(result);
      } catch (err) {
        setGeneralError(err);
      }
    }
  }, [createProgram, program, onApply, setValidationErrors, setGeneralError]);

  const handleClose = useCallback(() => {
    console.log(program);
    if (program) {
      onCancel();
    } else {
      showConfirm(
        "Discard Changes?",
        "You have made changes to this program without saving.  Do you wish to discard these changes and go back to the program list?",
        {
          onConfirm: () => {
            onCancel();
          },
        },
      );
    }
  }, [program, showConfirm, onCancel]);

  const handleBlur = (field, value) => {
    const errors = validateProgramField(field, value);
    if (notBlank(errors)) {
      addValidationError(errors);
    } else {
      removeValidationError(field);
    }
  };

  const handleTextChange = path => event => {
    const value = notBlank(event.target) ? event.target.value : event;
    setProgram(prev => set(lensPath(path), value, prev));
  };

  return (
    <Dialog open={open}>
      <DialogTitle id="edit-organization-detail-dialog-title">
        Enter Program Details
      </DialogTitle>
      <DialogContent>
        <DialogErrors />
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <TextField
              id="program-name"
              fullWidth
              label="Name"
              value={program.programName || ""}
              onChange={handleTextChange(["programName"])}
              error={hasValidationError("programName")}
              helperText={firstValidationError("programName")}
              onBlur={() => handleBlur("programName", program.programName)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              id="program-key"
              fullWidth
              label="Key"
              value={program.programKey || ""}
              onChange={handleTextChange(["programKey"])}
              error={hasValidationError("programKey")}
              helperText={firstValidationError("programKey")}
              onBlur={() => handleBlur("programKey", program.programKey)}
            />
          </Grid>
          <Grid item xs={12}>
            <OrganizationAutoComplete
              value={program.organizationId}
              onChange={handleTextChange(["organizationId"])}
              onBlur={() =>
                handleBlur("organizationId", program.organizationId)
              }
              error={hasValidationError("organizationId")}
              helperText={firstValidationError("organizationId")}
            />
          </Grid>
        </Grid>
        <ConfirmDialog handler={confirmHandler} />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Cancel
        </Button>
        <Button onClick={handleCreate} color="primary">
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewProgramDialog;
